import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import Modal from 'react-modal';
import Logo from '../images/school_logo.png';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';

Modal.setAppElement('#root');

const Sessions = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [newSession, setNewSession] = useState({ name: '' });
  const [editingSession, setEditingSession] = useState(null);
  const [currentSection, setCurrentSection] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isFetchingSessions, setIsFetchingSessions] = useState(true);

  const sessionsCollectionRef = collection(db, "sessions");

  useEffect(() => {
    fetchSessions();
  }, []);

  useEffect(() => {
    const filtered = sessions.filter((session) =>
      session.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredSessions(filtered);
  }, [searchTerm, sessions]);

  const fetchSessions = async () => {
    setIsFetchingSessions(true);
    try {
      const data = await getDocs(sessionsCollectionRef);
      const sessionList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setSessions(sessionList);
    } catch (error) {
      setError("Failed to fetch sessions");
      setShowModal(true);
    } finally {
      setIsFetchingSessions(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewSession({ ...newSession, [name]: value });
  };

  const handleAddSession = async () => {
    if (!newSession.name.trim()) {
      setError("Session name cannot be empty");
      setShowModal(true);
      return;
    }
    setUpdate(true);
    try {
      await addDoc(sessionsCollectionRef, {
        ...newSession,
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Session added successfully");
      fetchSessions();
      setNewSession({ name: '' });
      setIsAddModalOpen(false);
    } catch (error) {
      setError("Failed to add session");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateSession = async () => {
    setUpdate(true);
    try {
      const sessionDoc = doc(db, "sessions", editingSession.id);
      await updateDoc(sessionDoc, newSession);
      setSuccessMessage("Session updated successfully");
      fetchSessions();
      setEditingSession(null);
      setNewSession({ name: '' });
      setIsEditModalOpen(false);
    } catch (error) {
      setError("Failed to update session");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleEditSession = (sessionToEdit) => {
    setEditingSession(sessionToEdit);
    setNewSession({ name: sessionToEdit.name });
    setIsEditModalOpen(true);
  };

  const confirmDeleteSession = (sessionToDelete) => {
    setCurrentSection(sessionToDelete);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteSession = async (id) => {
    setUpdate(true);
    try {
      const sessionDoc = doc(db, "sessions", id);
      await deleteDoc(sessionDoc);
      setSuccessMessage("Session deleted successfully");
      fetchSessions();
    } catch (error) {
      setError("Failed to delete session");
    } finally {
      setUpdate(false);
      setIsDeleteModalOpen(false);
      setShowModal(true);
    }
  };

  if (loading) return <Loader text={"LOADING"} />;
  if (!userData) return <Loader text={"READING"} />;

  const allowedRoles = ['Admin', 'Cashier'];

  return (
    <>
      {update && <Loader text={"PROCESSING"} />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <div className="column align-center justify-center">
              <img src={Logo} className="middle-logo" alt="logo" />
            </div>
            <Link to="#" className="home-link" onClick={() => fetchSessions()}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items column align-center scroll-fixed">
            <h2 className="manage-page-heading caps size4">Manage Sessions</h2>
            <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Session</button>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {isFetchingSessions ? (
                  <p className="loading-text">Loading sessions ...</p>
                ) : filteredSessions.length === 0 ? (
                  <p className="loading-text">No sessions match your search.</p>
                ) : (
                  filteredSessions.map((sessionItem) => (
                    <div key={sessionItem.id} className="class-item">
                      <div className="row gap-2 align-center">
                        <p>{sessionItem.name}</p>
                      </div>
                      <div className="row align-center justify-center gap-15">
                        <button className="list-btn" onClick={() => handleEditSession(sessionItem)}>
                          <i className="fas fa-edit"></i>
                        </button>
                        {/* <button className="list-btn" onClick={() => confirmDeleteSession(sessionItem)}>
                          <i className="fas fa-trash-alt"></i>
                        </button> */}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      {/* Add Session Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onRequestClose={() => setIsAddModalOpen(false)}
        contentLabel="Add Session"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Add Session</h2>
        <form className="session-form">
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Session Name e.g. 2024/2025"
            value={newSession.name}
            onChange={handleInputChange}
          />
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleAddSession}>Add Session</button>
          </div>
        </form>
      </Modal>

      {/* Edit Session Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => {
          setIsEditModalOpen(false);
          setNewSession({ name: '' }); // Reset newSession
          setEditingSession(null); // Reset editingSession
        }}
        contentLabel="Edit Session"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Edit Session</h2>
        <form className="session-form">
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Session Name e.g. 2024/2025"
            value={newSession.name}
            onChange={handleInputChange}
          />
          <div className="row align-center justify-center gap-1">
            <button className="login-button" type="button" onClick={() => {
                setIsEditModalOpen(false);
                setNewSession({ name: '' }); // Reset newSession
                setEditingSession(null); // Reset editingSession
              }}>Cancel</button>
            <button className="login-button" type="button" onClick={handleUpdateSession}>Update Session</button>
          </div>
        </form>
      </Modal>

      {/* Delete Session Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        contentLabel="Delete Session"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Delete Session</h2>
        {currentSection && (
          <div>
            <p>Are you sure you want to delete <strong>{currentSection.name}</strong>?</p>
            <div className="row align-center gap-1">
              <button className="login-button" type="button" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
              <button className="login-button" type="button" onClick={() => handleDeleteSession(currentSection.id)}>Delete</button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default Sessions;
